import SiteInertiaController from '@/actions/App/Http/Controllers/SiteInertiaController';
import { RichContentRenderer } from '@/components/seo-content-renderer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from '@/components/ui/carousel';
import { Drawer, DrawerContent, DrawerHeader } from '@/components/ui/drawer';
import { Skeleton } from '@/components/ui/skeleton';
import { Typo } from '@/components/ui/typography';
import { useIsMobile } from '@/hooks/use-mobile';
import { get } from '@/lib/axios';
import { cn } from '@/lib/utils';
import { type RateDetail } from '@/types/search';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

interface RateDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mappingCode: string | null;
  onSelectServices?: (rateDetail: RateDetail) => void;
}

const LOAD_ERROR_MESSAGE = 'Không thể tải thông tin chi tiết';

const CONTENT_PROSE_CLASSES = cn(
  'prose prose-gray max-w-none',
  'prose-headings:font-semibold prose-headings:tracking-wide prose-headings:uppercase prose-headings:text-gray-900',
  'prose-h2:mt-8 prose-h2:mb-3 prose-h2:border-b-0 prose-h2:pb-0 prose-h2:text-base',
  'prose-h3:mt-6 prose-h3:mb-2 prose-h3:text-sm prose-h4:text-sm prose-h5:text-sm prose-h6:text-sm',
  'prose-p:my-2 prose-p:[&:not(:first-child)]:mt-2 prose-p:leading-relaxed prose-p:text-gray-700',
  'prose-ul:my-3 prose-ul:ml-0 prose-ul:pl-5 prose-ol:my-3 prose-ol:ml-0 prose-ol:pl-5',
  'prose-ul:[&>li]:mt-1 prose-ol:[&>li]:mt-1 prose-li:mx-0 prose-li:my-1 prose-li:text-gray-700',
  'prose-img:my-4 prose-img:rounded-xl prose-img:shadow-sm',
  'prose-strong:font-semibold prose-strong:text-gray-900',
);

/** Mirrors RichContentRenderer's emptiness check so we can fall back to Description. */
function hasLexicalContent(content: string | null | undefined): boolean {
  if (!content || content.trim() === '') return false;
  try {
    const parsed = JSON.parse(content);
    return Boolean(parsed?.root?.children?.length);
  } catch {
    return false;
  }
}

export function RateDetailDrawer({ open, onOpenChange, mappingCode }: RateDetailDrawerProps) {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [rateDetail, setRateDetail] = useState<RateDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchRateDetail = async (code: string) => {
    setLoading(true);
    setError(null);
    setRateDetail(null);

    try {
      const response = await get<RateDetail>(SiteInertiaController.rateDetail(code).url);

      if (response.success && response.data) {
        setRateDetail(response.data);
      } else {
        setError(response.message || LOAD_ERROR_MESSAGE);
        toast.error(response.message || LOAD_ERROR_MESSAGE);
      }
    } catch (err) {
      console.error('Failed to fetch rate detail:', err);
      setError('Có lỗi xảy ra khi tải thông tin');
      toast.error('Có lỗi xảy ra khi tải thông tin');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && mappingCode) {
      fetchRateDetail(mappingCode);
    }
  }, [open, mappingCode]);

  const hasDiscount = !!rateDetail && rateDetail.RateDiscountPercent > 0;
  const rateName = rateDetail ? rateDetail.DisplayName || rateDetail.Name : '';

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction={isMobile ? 'bottom' : 'right'}>
      <DrawerContent className="z-[52] h-full overflow-hidden rounded-l-3xl p-4 data-[vaul-drawer-direction=right]:sm:max-w-4xl md:px-8 md:py-8">
        <DrawerHeader className="px-0 pr-10">
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/3" />
            </div>
          ) : rateDetail ? (
            <div className="text-left">
              <div className="flex flex-wrap items-start gap-2">
                <Typo variant="h3" className="min-w-0 [overflow-wrap:anywhere]">
                  {rateName}
                </Typo>
                {hasDiscount && (
                  <Badge variant="destructive" className="mt-1 shrink-0">
                    -{rateDetail.RateDiscountPercent}%
                  </Badge>
                )}
              </div>
              {rateDetail.TypeName && (
                <Typo variant="muted" className="mt-1">
                  {rateDetail.TypeName}
                </Typo>
              )}
            </div>
          ) : null}
        </DrawerHeader>

        <div className="mt-2 flex-1 space-y-6 overflow-y-auto">
          {loading ? (
            <RateDetailSkeleton />
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Typo variant="muted" className="mb-4">
                {error}
              </Typo>
              <Button variant="outline" onClick={() => mappingCode && fetchRateDetail(mappingCode)}>
                Thử lại
              </Button>
            </div>
          ) : rateDetail ? (
            <>
              {rateDetail.Images?.length > 0 && <RateImageCarousel images={rateDetail.Images} alt={rateName} />}
              <RateDetailContent rateDetail={rateDetail} />
            </>
          ) : null}
        </div>

        <Button
          variant="outline"
          className="absolute top-4 right-4 size-8 rounded-lg"
          size="icon"
          aria-label="Đóng"
          onClick={() => onOpenChange(false)}
        >
          <X className="h-4 w-4" />
        </Button>
      </DrawerContent>
    </Drawer>
  );
}

function RateImageCarousel({ images, alt }: { images: string[]; alt: string }) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const hasMultiple = images.length > 1;

  useEffect(() => {
    if (!api) return;

    const onSelect = () => setCurrent(api.selectedScrollSnap());
    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onSelect);

    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
    };
  }, [api]);

  const navButtonClass =
    'border-0 bg-white/80 text-foreground shadow-md backdrop-blur-sm hover:bg-white focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-0 size-9';

  return (
    <Carousel setApi={setApi} opts={{ loop: hasMultiple }} className="w-full">
      <CarouselContent>
        {images.map((src, index) => (
          <CarouselItem key={`${src}-${index}`}>
            <img
              src={src}
              alt={alt}
              loading={index === 0 ? 'eager' : 'lazy'}
              decoding="async"
              className="bg-muted aspect-[12/5] w-full rounded-2xl object-cover"
            />
          </CarouselItem>
        ))}
      </CarouselContent>

      {hasMultiple && (
        <>
          <CarouselPrevious className={cn(navButtonClass, 'left-3')} />
          <CarouselNext className={cn(navButtonClass, 'right-3')} />
          <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
            <div className="pointer-events-auto flex items-center gap-1.5 rounded-full bg-black/25 px-2 py-1 backdrop-blur-sm">
              {images.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  aria-label={`Ảnh ${index + 1}`}
                  aria-current={index === current}
                  onClick={() => api?.scrollTo(index)}
                  className={cn(
                    'h-1.5 rounded-full transition-[width,background-color] duration-200 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none',
                    index === current ? 'w-4 bg-white' : 'w-1.5 bg-white/60 hover:bg-white/90',
                  )}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </Carousel>
  );
}

function RateDetailContent({ rateDetail }: { rateDetail: RateDetail }) {
  if (hasLexicalContent(rateDetail.Content)) {
    return <RichContentRenderer editorSerializedState={rateDetail.Content} contentClassName={CONTENT_PROSE_CLASSES} />;
  }

  if (rateDetail.Description?.trim()) {
    return <p className="text-sm leading-relaxed whitespace-pre-line text-gray-700">{rateDetail.Description}</p>;
  }

  return (
    <div className="border-border rounded-2xl border border-dashed py-10 text-center">
      <Typo variant="muted">Chưa có thông tin chi tiết</Typo>
    </div>
  );
}

function RateDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="aspect-[12/5] w-full rounded-2xl" />
      <div className="space-y-3">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  );
}
